import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Search, 
  AlertTriangle, 
  Activity, 
  FileText, 
  CheckSquare,
  ArrowRight,
  Terminal,
  ShieldAlert
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Process() {
  const navigate = useNavigate();
  const steps = [
    {
      step: "01",
      title: "Définition du périmètre",
      desc: "Cartographie de vos actifs critiques et infrastructures cibles pour encadrer légalement l'audit.",
      icon: ShieldCheck,
      threatPhase: "Cadrage initial",
      // Intensité du risque : Neutre / Organisation
      threatColor: "text-slate-500 bg-slate-100 border border-slate-200"
    },
    {
      step: "02",
      title: "Analyse & collecte",
      desc: "Aspiration passive et active (OSINT) pour identifier votre surface d’attaque accessible publiquement.",
      icon: Search,
      threatPhase: "Signal faible",
      // Intensité du risque : Informationnel
      threatColor: "text-blue-600 bg-blue-50 border border-blue-100"
    },
    {
      step: "03",
      title: "Identification des failles",
      desc: "Détection des vulnérabilités, ports ouverts et identifiants compromis directement exploitables.",
      icon: AlertTriangle,
      threatPhase: "Exposition critique",
      // Intensité du risque : Sévérité Moyenne / Haute
      threatColor: "text-amber-600 bg-amber-50 border border-amber-100"
    },
    {
      step: "04",
      title: "Évaluation des risques",
      desc: "Simulation d'attaques réelles pour mesurer l'impact concret sur vos données et comptes privilèges.",
      icon: Activity,
      threatPhase: "Danger d'intrusion",
      // Intensité du risque : Sévérité Haute / Compromission
      threatColor: "text-orange-600 bg-orange-50 border border-orange-200"
    },
    {
      step: "05",
      title: "Rapport détaillé",
      desc: "Restitution technique documentée avec preuves de concept (PoC) classées par sévérité CVSS v3.",
      icon: FileText,
      threatPhase: "Preuves d'impact",
      // Intensité du risque : Restitution Critique
      threatColor: "text-red-600 bg-red-50 border border-red-100"
    },
    {
      step: "06",
      title: "Recommandations",
      desc: "Livraison d'un plan de remédiation priorisé pour neutraliser durablement les vecteurs de risque.",
      icon: CheckSquare,
      threatPhase: "Remédiation",
      // Résolution : Retour à l'état sécurisé
      threatColor: "text-green-600 bg-green-50 border border-green-100"
    },
  ];

  return (
    <section className="w-full bg-[#f8fafc] py-14 px-6 md:py-16 relative overflow-hidden">
      
      {/* Maillage Cyber discret */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-30 pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        
        {/* En-tête Compact */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest mb-3 shadow-sm">
            <Terminal size={10} className="text-blue-400" /> Cadre Méthodologique Nodixs
          </div>
          <h2 className="text-[#0f172a] text-2xl md:text-3xl font-black tracking-tight mb-2 font-inter">
            Comment se déroule une mission ?
          </h2>
          <p className="text-slate-500 text-xs md:text-sm font-medium max-w-xl mx-auto leading-relaxed">
            Notre processus d’audit en 6 étapes : une méthodologie rigoureuse alignée sur les exigences de gouvernance et de maîtrise des risques cyber.
          </p>
        </motion.div>

        {/* Grille des 6 étapes avec gradation des risques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ 
                  y: -3, 
                  borderColor: '#3b82f6', 
                  boxShadow: '0 12px 25px -10px rgba(59, 130, 246, 0.06)' 
                }}
                className="relative bg-white border border-slate-200/90 p-5 md:p-6 rounded-xl flex flex-col justify-between transition-all duration-200 group overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-transparent via-blue-500 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                
                <div>
                  {/* Ligne Supérieure */}
                  <div className="w-full flex items-center justify-between mb-4">
                    <span className="text-slate-400 text-[10px] font-mono font-bold bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                      Étape // {step.step}
                    </span>
                    <div className="p-2 rounded-lg bg-slate-900 text-white group-hover:bg-blue-600 transition-colors shadow-sm">
                      <Icon size={15} strokeWidth={2} />
                    </div>
                  </div>

                  {/* Titre & Description */}
                  <h3 className="text-[#0f172a] text-base font-black mb-1.5 font-inter leading-snug group-hover:text-blue-900 transition-colors">
                    {step.title}
                  </h3>
                  
                  <p className="text-slate-500 text-[12px] leading-relaxed font-medium mb-4">
                    {step.desc}
                  </p>
                </div>

                {/* Niveau d'impact (UX Lexicale Pro & Narration du Risque) */}
                <div className="pt-2.5 border-t border-slate-100 w-full flex items-center justify-between">
                  <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider">Niveau :</span>
                  <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded tracking-wide ${step.threatColor}`}>
                    {step.threatPhase}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Micro-trigger de tension psychologique rééquilibré */}
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-slate-600 text-[11px] font-bold mt-8 flex items-center justify-center gap-1.5"
        >
          <ShieldAlert size={12} className="text-blue-500" />
          Chaque étape affine et cartographie votre niveau d'exposition réel face aux menaces actives.
        </motion.p>

        {/* Bloc CTA Compact et Métier */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border-2 border-slate-900 p-5 md:p-6 rounded-2xl mt-8 shadow-[0_8px_20px_-12px_rgba(0,0,0,0.02)] flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <div className="text-center sm:text-left">
            <h4 className="text-[#0f172a] text-base font-black mb-0.5">
              Prêt à sécuriser vos actifs critiques ?
            </h4>
            <p className="text-slate-500 text-[12px] font-medium">
              Mesurez gratuitement votre première ligne de défense avant qu'un tiers ne l'exploite.
            </p>
          </div>
          
          <button 
            onClick={() => navigate('/rdv')}
            whileHover={{ 
              y: -3, 
              borderColor: '#3b82f6', 
              boxShadow: '0 12px 25px -10px rgba(59, 130, 246, 0.06)' 
            }}
          className="whitespace-nowrap bg-slate-900 hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95 shadow-sm group">
            Lancer mon audit de sécurité gratuit
            <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </motion.div>

      </div>
    </section>
  );
}